import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css'

import { useWindowWidth } from '@react-hook/window-size/throttled'
import classNames from 'classnames'
import Link from 'next/link'
import { useEffect, useState } from 'react'
import Card from 'react-bootstrap/Card'
import BootstrapTable from 'react-bootstrap-table-next'
import paginationFactory, {
  PaginationListStandalone,
  PaginationProvider,
  PaginationTotalStandalone,
  SizePerPageDropdownStandalone,
} from 'react-bootstrap-table2-paginator'
import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit'
import { v4 } from 'uuid'

import artifact from '@/assets/artifact.svg'
import creature from '@/assets/creature.svg'
import enchantment from '@/assets/enchantment.svg'
import instant from '@/assets/instant.svg'
import planeswalker from '@/assets/planeswalker.svg'
import salt_48 from '@/assets/salt_48.png'
import sorcery from '@/assets/sorcery.svg'
import Spinner from '@/components/Spinner/Spinner'
import { DeckTableEntryType } from '@/types/page'
import { desktopWidth } from '@/utils/constants'

import styles from './DecksTable.module.scss'

const { SearchBar } = Search

const customTotal = (from: number, to: number, size: number) => (
  <span className='react-bootstrap-table-pagination-total'>
    Showing {from} to {to} of {size} decks
  </span>
)

const sizePerPageRenderer = ({
  options,
  currSizePerPage,
  onSizePerPageChange,
}: {
  options: Array<{ page: number; text: string }>
  currSizePerPage: string
  onSizePerPageChange: (n: number) => void
}) => (
  <div className='btn-group' role='group'>
    {options.map((option) => {
      const isSelect = currSizePerPage === `${option.page}`
      return (
        <button
          key={option.text}
          type='button'
          onClick={() => onSizePerPageChange(option.page)}
          className={`btn ${isSelect ? 'btn-primary' : 'btn-secondary'}`}
        >
          {option.text}
        </button>
      )
    })}
  </div>
)

const paginationOptions = {
  custom: true,
  disablePageTitle: true,
  firstPageTitle: 'Next page',
  nextPageText: 'Next',
  nextPageTitle: 'First page',
  paginationTotalRenderer: customTotal,
  prePageText: 'Back',
  prePageTitle: 'Pre page',
  showTotal: true,
  sizePerPageList: [
    {
      text: '10',
      value: 10,
    },
    {
      text: '25',
      value: 25,
    },
    {
      text: '50',
      value: 50,
    },
    {
      text: '100',
      value: 100,
    },
  ],
  sizePerPageRenderer,
  withFirstAndLast: false,
}

const urlhashFormatter = (cell: string, _row: DeckTableEntryType) => (
  <Link href={`/deckpreview/${cell}`}>View Decklist</Link>
)

const priceFormatter = (cell: string, _row: DeckTableEntryType) => `$${cell}`

const saltFormatter = (cell: number, _row: DeckTableEntryType) => (
  <span>
    <img alt='Salt Score' className={styles.image} src={salt_48.src} title='Salt Score' />
    {cell?.toFixed(2)}
  </span>
)

const creatureFormatter = (cell: string, _row: DeckTableEntryType) => (
  <span>
    <img alt='Creature' className={styles.image} src={creature.src} title='Creature' />
    {cell}
  </span>
)

const instantFormatter = (cell: string, _row: DeckTableEntryType) => (
  <span>
    <img alt='Instant' className={styles.image} src={instant.src} title='Instant' />
    {cell}
  </span>
)

const sorceryFormatter = (cell: string, _row: DeckTableEntryType) => (
  <span>
    <img alt='Sorcery' className={styles.image} src={sorcery.src} title='Sorcery' />
    {cell}
  </span>
)

const artifactFormatter = (cell: string, _row: DeckTableEntryType) => (
  <span>
    <img alt='Artifact' className={styles.image} src={artifact.src} title='Artifact' />
    {cell}
  </span>
)

const enchantmentFormatter = (cell: string, _row: DeckTableEntryType) => (
  <span>
    <img alt='Enchantment' className={styles.image} src={enchantment.src} title='Enchantment' />
    {cell}
  </span>
)

const planeswalkerFormatter = (cell: string, _row: DeckTableEntryType) => (
  <span>
    <img alt='Planeswalker' className={styles.image} src={planeswalker.src} title='Planeswalker' />
    {cell}
  </span>
)

const tagsFormatter = (_cell: string, row: DeckTableEntryType) => (
  <div className='d-grid'>{row.tags?.sort().map((item) => <span key={v4()}>{item}</span>)}</div>
)

const columnsMobile = [
  {
    dataField: 'urlhash',
    formatter: urlhashFormatter,
    text: '',
  },
  {
    dataField: 'price',
    formatter: priceFormatter,
    sort: true,
    text: 'Price',
  },
  {
    dataField: 'tags',
    formatter: tagsFormatter,
    sort: true,
    text: 'Tag',
  },
  {
    dataField: 'savedate',
    sort: true,
    text: 'Save Date',
  },
]

const columns = [
  {
    dataField: 'urlhash',
    formatter: urlhashFormatter,
    text: '',
  },
  {
    dataField: 'price',
    formatter: priceFormatter,
    sort: true,
    text: 'Price',
  },
  {
    dataField: 'tags',
    formatter: tagsFormatter,
    text: 'Tags',
  },
  {
    dataField: 'salt',
    formatter: saltFormatter,
    sort: true,
    text: 'Salt',
  },
  {
    dataField: 'creature',
    formatter: creatureFormatter,
    sort: true,
    text: 'Creature',
  },
  {
    dataField: 'instant',
    formatter: instantFormatter,
    sort: true,
    text: 'Instant',
  },
  {
    dataField: 'sorcery',
    formatter: sorceryFormatter,
    sort: true,
    text: 'Sorcery',
  },
  {
    dataField: 'artifact',
    formatter: artifactFormatter,
    sort: true,
    text: 'Artifact',
  },
  {
    dataField: 'enchantment',
    formatter: enchantmentFormatter,
    sort: true,
    text: 'Enchantment',
  },
  {
    dataField: 'planeswalker',
    formatter: planeswalkerFormatter,
    sort: true,
    text: 'Planeswalker',
  },
  {
    dataField: 'savedate',
    sort: true,
    text: 'Save Date',
  },
]

type Props = {
  rows: DeckTableEntryType[]
}

const DecksTable = ({ rows }: Props) => {
  const [isClient, setIsClient] = useState(false)
  const windowWidth = useWindowWidth()
  const isDesktop = windowWidth >= desktopWidth

  useEffect(() => {
    setIsClient(true)
  }, [])

  return (
    <Card className='mb-3 shadow-sm'>
      <Card.Body>
        {!isClient ? (
          <div className='align-items-center d-flex flex-column h-100 my-5'>
            <Spinner size='xl' />
          </div>
        ) : rows ? (
          <PaginationProvider pagination={paginationFactory(paginationOptions)}>
            {({ paginationProps, paginationTableProps }) => (
              <ToolkitProvider keyField='urlhash' data={rows} columns={isDesktop ? columns : columnsMobile} search>
                {(toolkitProps) => (
                  <div className={styles.table}>
                    <div className={classNames('mb-3', styles.row)}>
                      <SizePerPageDropdownStandalone {...paginationProps} />
                      <SearchBar {...toolkitProps.searchProps} />
                    </div>
                    <BootstrapTable
                      {...toolkitProps.baseProps}
                      {...paginationTableProps}
                      headerWrapperClasses={styles.header}
                      striped
                    />
                    <div className={styles.row}>
                      <PaginationTotalStandalone {...paginationProps} />
                      <PaginationListStandalone {...paginationProps} />
                    </div>
                  </div>
                )}
              </ToolkitProvider>
            )}
          </PaginationProvider>
        ) : (
          <div className='text-center'>No results</div>
        )}
      </Card.Body>
    </Card>
  )
}

export default DecksTable
